import type { Saga, SagaIterator } from 'redux-saga';
import { call, takeEvery } from 'redux-saga/effects';

import { getLogger } from '../../utils/core';
import * as actions from './bumpActions';
import { send } from './bumpInstance';

const log = getLogger('flux/bump/bumpSagas');

type BumpUpdateType = 'CHANGE_DUMP' | 'CHANGE_NUDGE' | 'JOB_STATUS';
type ResourceUpdaters = Partial<Record<BumpUpdateType, Saga>>;

const RESOURCE_UPDATERS: Record<string, ResourceUpdaters> = {};

// !!! important - NEVER subscribe from shared package to avoid multi-instance subscription conflicts
// subscribe only from main client code side
export const subscribeForBumpUpdates = (
  resourceType: string,
  dumpUpdater?: Saga,
  nudgeUpdater?: Saga,
  jobUpdater?: Saga,
): void => {
  RESOURCE_UPDATERS[resourceType] = {
    CHANGE_DUMP: dumpUpdater,
    CHANGE_NUDGE: nudgeUpdater,
    JOB_STATUS: jobUpdater,
  };
};

function* receiveBumpMessage({ payload: message }: ReturnType<typeof actions.receivedBumpMessage>): SagaIterator {
  if (message.type) {
    switch (message.type) {
      case 'JOB_STATUS': {
        const updater = message.data?.jobType ? RESOURCE_UPDATERS[message.data.jobType]?.JOB_STATUS : undefined;
        if (updater) {
          yield call(updater, message.data);
        }
        if (!updater) {
          log.info('Unhandled bump jobType', message.data?.jobType);
        }
        break;
      }
      case 'CHANGE_DUMP':
      case 'CHANGE_NUDGE': {
        const updater = message.data?.resourceType
          ? RESOURCE_UPDATERS[message.data.resourceType]?.[message.type]
          : undefined;
        if (updater) {
          yield call(updater, message.data?.resources);
        }
        if (!updater) {
          log.info('Unhandled bump resource', message.data?.resourceType);
        }
        break;
      }
      default:
        log.info('Unhandled bump message type', message.type, message);
    }
  } else if (message.eventType) {
    const updater = RESOURCE_UPDATERS[message.eventType]?.CHANGE_NUDGE;
    if (updater) {
      yield call(updater, message);
    }
  } else {
    log.info('Unhandled bump message', message);
  }
}

function* sendBumpMessage({ payload: message }: ReturnType<typeof actions.sendBumpMessage>): SagaIterator {
  yield call(send, typeof message === 'string' ? message : JSON.stringify(message));
}

function* receiveBumpMessageActionWatcher(): SagaIterator {
  yield takeEvery(actions.receivedBumpMessage, receiveBumpMessage);
}

function* sendBumpMessageActionWatcher(): SagaIterator {
  yield takeEvery(actions.sendBumpMessage, sendBumpMessage);
}

export default [receiveBumpMessageActionWatcher, sendBumpMessageActionWatcher];
